import { useState, useEffect } from 'react';
import { Shield, Lock, ArrowLeft } from 'lucide-react';
import { Routes, Route } from 'react-router-dom';
import type { User } from '@supabase/supabase-js';
import { supabase } from '../utils/supabaseStorage';
import {
  ADMIN_AUTH_KEY,
  ADMIN_SESSION_KEY,
  clearAdminSessionStorage,
  parseAdminStaffSession,
  setAdminSessionPersisted,
  type AdminStaffSession,
} from '../utils/adminSession';
import { CreateModelPage } from './CreateModelPage';
import { ModelsPage } from './ModelsPage';
import { UsersPage } from './UsersPage';
import { ProFormsPage } from './ProFormsPage';
import { CreateProFormPage } from './CreateProFormPage';
import { ProMaxModelsPage } from './ProMaxModelsPage';
import { ProMaxModelFormPage } from './ProMaxModelFormPage';
import { AgentsPage } from './AgentsPage';
import { AgentTemplateFormPage } from './AgentTemplateFormPage';
import { AgentAutogenerateSettingsPage } from './AgentAutogenerateSettingsPage';
import { AgentInstancesPage } from './AgentInstancesPage';
import { AdminBillingPage } from './AdminBillingPage';
import { CreatorsPage } from './CreatorsPage';
import { CreatorReviewQueuePage } from './CreatorReviewQueuePage';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { AppBoard } from '../components/AppBoard';
import { AdminLayout } from '../components/admin/AdminLayout';

const AdminHomePage = () => (
  <div className="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden">
    <main className="max-w-6xl mx-auto w-full min-h-0 flex-1 overflow-y-auto overscroll-y-contain px-4 py-8">
      <AppBoard />
    </main>
  </div>
);

function looksLikeAuthUserUuid(s: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(s.trim());
}

/** Turn GoTrue / network errors into something actionable in the UI. */
function formatAuthSignInError(err: { message?: string; status?: number; code?: string }): string {
  const m = (err.message || 'Sign-in failed').trim();
  const lower = m.toLowerCase();
  const code = (err.code || '').toLowerCase();

  if (code === 'email_not_confirmed' || lower.includes('email not confirmed')) {
    return `${m} — In Dashboard: Authentication → Providers → Email, turn off "Confirm email" for testing, or open the confirmation link in your inbox.`;
  }
  if (
    lower.includes('invalid login') ||
    lower.includes('invalid credentials') ||
    code === 'invalid_credentials'
  ) {
    return `${m} — Ensure this email exists under Authentication → Users and the password matches that account. If your operator password is different, fill in "Supabase password" below.`;
  }
  if (lower.includes('email provider is disabled') || lower.includes('signup is disabled')) {
    return `${m} — Enable the Email provider: Authentication → Providers → Email.`;
  }
  if (err.status === 400) {
    return `${m} — HTTP 400 is usually wrong email/password, unconfirmed email, or Email sign-in disabled in the project.`;
  }
  return m;
}

function normalizeDbRole(role: unknown): string {
  return String(role ?? '')
    .trim()
    .toLowerCase();
}

function isStaffRole(roleNorm: string): roleNorm is 'admin' | 'superadmin' {
  return roleNorm === 'admin' || roleNorm === 'superadmin';
}

/**
 * Staff session for the signed-in auth user. Prefer RPC (bypasses RLS / hy_user_id mismatch + email fallback);
 * then fall back to direct select when the row is readable by RLS (hy_user_id = auth id).
 */
async function buildStaffIfEligible(user: User): Promise<AdminStaffSession | null> {
  const { data: rpcData, error: rpcErr } = await supabase.rpc('get_staff_profile_for_session');
  if (!rpcErr && rpcData != null && typeof rpcData === 'object') {
    const row = rpcData as { hy_name?: string | null; role?: string | null };
    const roleNorm = normalizeDbRole(row.role);
    if (isStaffRole(roleNorm)) {
      const displayName =
        (typeof row.hy_name === 'string' && row.hy_name.trim()) ||
        user.email?.split('@')[0] ||
        'Admin';
      return {
        hy_user_id: user.id,
        name: displayName,
        role: roleNorm,
      };
    }
  }
  if (rpcErr) {
    const m = (rpcErr.message || '').toLowerCase();
    if (!m.includes('does not exist') && !m.includes('unknown') && !m.includes('schema cache')) {
      console.warn('[AdminPage] get_staff_profile_for_session:', rpcErr.message);
    }
  }

  const { data: profile, error: profErr } = await supabase
    .from('users')
    .select('hy_name, role')
    .eq('hy_user_id', user.id)
    .maybeSingle();
  if (profErr) {
    console.warn('[AdminPage] users profile:', profErr.message);
    return null;
  }
  const roleNorm = normalizeDbRole(profile?.role);
  if (!isStaffRole(roleNorm)) return null;
  const displayName =
    (typeof profile?.hy_name === 'string' && profile.hy_name.trim()) ||
    user.email?.split('@')[0] ||
    'Admin';
  return {
    hy_user_id: user.id,
    name: displayName,
    role: roleNorm,
  };
}

export const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [signInEmail, setSignInEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  // Restore admin shell: (1) existing admin localStorage + valid session, (2) any logged-in user with staff role (auto-login).
  useEffect(() => {
    const restore = async () => {
      const legacyAuth = sessionStorage.getItem('admin_authenticated');
      const legacyAccess = sessionStorage.getItem('admin_access_record');
      if (legacyAuth === 'true' && legacyAccess) {
        localStorage.setItem(ADMIN_AUTH_KEY, 'true');
        localStorage.setItem(ADMIN_SESSION_KEY, legacyAccess);
        sessionStorage.removeItem('admin_authenticated');
        sessionStorage.removeItem('admin_access_record');
      }

      const {
        data: { user },
        error: userErr,
      } = await supabase.auth.getUser();
      if (userErr) {
        console.warn('[AdminPage] getUser on restore:', userErr.message);
      }
      if (!user) {
        if (localStorage.getItem(ADMIN_AUTH_KEY) === 'true' && parseAdminStaffSession()) {
          clearAdminSessionStorage();
        }
        setIsLoading(false);
        return;
      }

      const record = parseAdminStaffSession();
      if (record && looksLikeAuthUserUuid(record.hy_user_id) && record.hy_user_id.trim() !== user.id.trim()) {
        clearAdminSessionStorage();
        await supabase.auth.signOut();
        setIsLoading(false);
        return;
      }

      const staff = await buildStaffIfEligible(user);
      if (!staff) {
        if (localStorage.getItem(ADMIN_AUTH_KEY) === 'true') {
          clearAdminSessionStorage();
        }
        setIsLoading(false);
        return;
      }

      setAdminSessionPersisted(staff);
      setIsAuthenticated(true);
      setIsLoading(false);
    };

    void restore();
  }, []);

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        clearAdminSessionStorage();
        setIsAuthenticated(false);
        return;
      }
      if (event === 'SIGNED_IN' && session?.user) {
        void (async () => {
          const staff = await buildStaffIfEligible(session.user);
          if (staff) {
            setAdminSessionPersisted(staff);
            setIsAuthenticated(true);
          } else {
            if (localStorage.getItem(ADMIN_AUTH_KEY) === 'true') {
              clearAdminSessionStorage();
            }
            setIsAuthenticated(false);
          }
        })();
      }
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsAuthenticating(true);
    setError('');

    try {
      const loginEmail = signInEmail.trim();
      if (!loginEmail) {
        setError('Email is required.');
        return;
      }

      const { data: signData, error: signErr } = await supabase.auth.signInWithPassword({
        email: loginEmail,
        password: password,
      });

      if (signErr) {
        setError(formatAuthSignInError(signErr));
        return;
      }

      const authUser = signData.user;
      if (!authUser?.id) {
        setError('Sign-in did not return a user.');
        await supabase.auth.signOut();
        return;
      }

      const staff = await buildStaffIfEligible(authUser);
      if (!staff) {
        await supabase.auth.signOut();
        setError(
          'Could not confirm staff access. If you are sure public.users.role is admin or superadmin, ' +
            'check that hy_user_id equals your Supabase Auth user id, or run migration that adds get_staff_profile_for_session (email fallback).'
        );
        return;
      }

      setIsAuthenticated(true);
      setAdminSessionPersisted(staff);
    } catch (error) {
      console.error('Authentication error:', error);
      setError('Authentication failed. Please try again.');
    } finally {
      setIsAuthenticating(false);
    }
  };

  const goBack = () => {
    window.history.back();
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-brand border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // Authentication form
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-elevated border border-subtle rounded-lg p-8 max-w-md w-full">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-brand rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-th-primary mb-2">
              Admin Access
            </h1>
            <p className="text-th-secondary">
              Sign in with a Supabase Auth account whose <span className="font-mono">public.users.role</span> is{' '}
              <span className="font-mono">admin</span> or <span className="font-mono">superadmin</span>.
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-th-primary">
                Email
              </label>
              <input
                type="email"
                value={signInEmail}
                onChange={(e) => setSignInEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full px-4 py-3 bg-elevated border border-subtle rounded-lg text-field placeholder-th-tertiary focus:border-brand focus:outline-none transition-colors"
                autoComplete="username"
                required
                disabled={isAuthenticating}
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-th-primary">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Supabase Auth password"
                className="w-full px-4 py-3 bg-elevated border border-subtle rounded-lg text-field placeholder-th-tertiary focus:border-brand focus:outline-none transition-colors"
                required
                disabled={isAuthenticating}
                autoComplete="current-password"
              />
            </div>

            {error && (
              <div className="p-3 bg-status-error/10 border border-status-error/20 rounded-lg">
                <p className="text-status-error text-sm">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isAuthenticating}
              className="w-full button-primary"
            >
              <div className="flex items-center justify-center space-x-2">
                {isAuthenticating ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <Lock className="w-5 h-5" />
                )}
                <span>{isAuthenticating ? 'Authenticating...' : 'Sign In'}</span>
              </div>
            </button>
          </form>

          {/* Back Button */}
          <div className="mt-6 pt-6 border-t border-subtle">
            <button
              onClick={goBack}
              className="w-full flex items-center justify-center space-x-2 py-2 text-th-secondary hover:text-th-primary transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to App</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route element={<AdminLayout />}>
        <Route index element={<AdminHomePage />} />
        <Route path="models" element={<ModelsPage />} />
        <Route path="create" element={<CreateModelPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="pro-forms" element={<ProFormsPage />} />
        <Route path="create-pro-form" element={<CreateProFormPage />} />
        <Route path="promax-models" element={<ProMaxModelsPage />} />
        <Route path="promax-models/create" element={<ProMaxModelFormPage />} />
        <Route path="promax-models/edit/:modelId" element={<ProMaxModelFormPage />} />
        <Route path="agents" element={<AgentsPage />} />
        <Route path="agents/create" element={<AgentTemplateFormPage />} />
        <Route path="agents/edit/:templateId" element={<AgentTemplateFormPage />} />
        <Route path="agents/autogen-settings" element={<AgentAutogenerateSettingsPage />} />
        <Route path="agents/instances" element={<AgentInstancesPage />} />
        <Route path="billing" element={<AdminBillingPage />} />
        <Route path="creators" element={<CreatorsPage />} />
        <Route path="creators/queue" element={<CreatorReviewQueuePage />} />
      </Route>
    </Routes>
  );
};

export default AdminPage;